import React from "react";
import { StyleSheet, View } from "react-native";
import { Text } from "native-base";
import { SwiperFlatList } from "react-native-swiper-flatlist";
import { webFont } from "../../Universal/API/API";
import { AutoWidthImage } from "../../Universal/Image/AutoWidthImage";
import PressableWrapp from "../../Universal/Buttons/PressableReact/PressableWrapp";

export interface CollectionTypeSectionV2 {
  key: string;
  label: string;
  count: number;
}

interface CollectionHeaderV2Props {
  collectionName: string;
  images: string[];
  totalUnitsCount: number;
  sections: CollectionTypeSectionV2[];
  selectedSectionKey: string;
  onSelectSection: (key: string) => void;
  linkPdf?: string;
  scaleAll: number;
  isSmallVersion: boolean;
  isMediumVersion: boolean;
  onPressImage: (image: string) => void;
  onOpenPdf?: () => void;
}

const createCollectionHeaderV2Styles = (scaleAll: number) =>
  StyleSheet.create({
    outer: {
      width: "100%",
      paddingTop: 12 * scaleAll,
      paddingBottom: 10 * scaleAll,
      paddingHorizontal: 16 * scaleAll,
    },
    shell: {
      width: "100%",
      maxWidth: 1480 * scaleAll,
      alignSelf: "center",
    },
    hero: {
      width: "100%",
      paddingHorizontal: 2 * scaleAll,
    },
    outlineChip: {
      minHeight: 44 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 7 * scaleAll,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#E9E4DC",
      justifyContent: "center",
      cursor: "pointer",
    },
    outlineChipText: {
      fontFamily: webFont("Inter"),
      fontSize: 13 * scaleAll,
      color: "#1A1A1A",
      fontWeight: "600",
    },
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 28 * scaleAll,
      lineHeight: 33 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      maxWidth: 1120 * scaleAll,
    },
    subtitle: {
      display: "none",
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
    },
    controlsRow: {
      marginTop: 14 * scaleAll,
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10 * scaleAll,
    },
    pillsRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8 * scaleAll,
      flex: 1,
    },
    filterPill: {
      minHeight: 42 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 7 * scaleAll,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#E7E1D8",
      flexDirection: "row",
      alignItems: "center",
      gap: 8 * scaleAll,
      cursor: "pointer",
    },
    filterPillActive: {
      backgroundColor: "#111111",
      borderColor: "#111111",
    },
    filterPillText: {
      fontFamily: webFont("Inter"),
      fontSize: 13 * scaleAll,
      color: "#1E1E1E",
      fontWeight: "600",
    },
    filterPillTextActive: {
      color: "#FFFFFF",
    },
    filterPillCount: {
      minWidth: 24 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 6 * scaleAll,
      paddingVertical: 3 * scaleAll,
      backgroundColor: "#F3EEE6",
      alignItems: "center",
      justifyContent: "center",
    },
    filterPillCountActive: {
      backgroundColor: "rgba(255,255,255,0.16)",
    },
    filterPillCountText: {
      fontFamily: webFont("Inter"),
      fontSize: 11 * scaleAll,
      color: "#514A42",
      fontWeight: "700",
    },
    filterPillCountTextActive: {
      color: "#FFFFFF",
    },
    galleryCard: {
      marginTop: 18 * scaleAll,
      borderRadius: 30 * scaleAll,
      backgroundColor: "#FBFBFA",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      overflow: "hidden",
    },
    galleryTopRow: {
      paddingHorizontal: 16 * scaleAll,
      paddingTop: 14 * scaleAll,
      paddingBottom: 10 * scaleAll,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10 * scaleAll,
      flexWrap: "wrap",
    },
    galleryTitle: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#6B665F",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.7,
    },
    galleryCount: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#44413C",
      fontWeight: "600",
    },
    swiper: {
      width: "100%",
      marginBottom: 12 * scaleAll,
    },
    imagePressable: {
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
    },
    emptyGallery: {
      minHeight: 180 * scaleAll,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 16 * scaleAll,
      paddingBottom: 14 * scaleAll,
    },
    emptyGalleryText: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      color: "#7A746C",
      fontWeight: "500",
      textAlign: "center",
    },
  });

const CollectionHeaderV2: React.FC<CollectionHeaderV2Props> = ({
  collectionName,
  images,
  totalUnitsCount,
  sections,
  selectedSectionKey,
  onSelectSection,
  linkPdf,
  scaleAll,
  isSmallVersion,
  isMediumVersion,
  onPressImage,
  onOpenPdf,
}) => {
  const styles = React.useMemo(() => createCollectionHeaderV2Styles(scaleAll), [scaleAll]);

  const galleryHeight = React.useMemo(() => {
    if (isSmallVersion) return 200 * scaleAll;
    if (isMediumVersion) return 320 * scaleAll;
    return 480 * scaleAll;
  }, [isMediumVersion, isSmallVersion, scaleAll]);

  const titleStyle = React.useMemo(
    () => [
      styles.title,
      isSmallVersion
        ? {
            fontSize: 22 * scaleAll,
            lineHeight: 27 * scaleAll,
          }
        : isMediumVersion
          ? {
              fontSize: 25 * scaleAll,
              lineHeight: 30 * scaleAll,
            }
          : null,
    ],
    [isMediumVersion, isSmallVersion, scaleAll, styles.title]
  );

  return (
    <View style={styles.outer}>
      <View style={styles.shell}>
        <View style={styles.hero}>
          <Text selectable={false} style={titleStyle}>
            {`Коллекция ${collectionName}`}
          </Text>

          <View style={styles.galleryCard}>
            <View style={styles.galleryTopRow}>
              <Text selectable={false} style={styles.galleryTitle}>
                Галерея
              </Text>
              <Text selectable={false} style={styles.galleryCount}>
                {images.length ? `${images.length} фото` : "Галерея пустая"}
              </Text>
            </View>

            {images.length ? (
              <SwiperFlatList
                data={images}
                centerContent
                keyExtractor={(item, index) => `${item}-${index}`}
                style={styles.swiper}
                renderItem={({ item }) => (
                  <PressableWrapp
                    activeOpacity={0.92}
                    style={styles.imagePressable}
                    onPress={() => onPressImage(item)}
                  >
                    <AutoWidthImage
                      height={galleryHeight}
                      scaleAll={scaleAll}
                      source={{ uri: item }}
                    />
                  </PressableWrapp>
                )}
              />
            ) : (
              <View style={styles.emptyGallery}>
                <Text selectable={false} style={styles.emptyGalleryText}>
                  Для этой коллекции пока не добавлены изображения.
                </Text>
              </View>
            )}
          </View>

          <View style={styles.controlsRow}>
            <View style={styles.pillsRow}>
              <PressableWrapp
                style={[
                  styles.filterPill,
                  selectedSectionKey === "all" && styles.filterPillActive,
                ]}
                onPress={() => onSelectSection("all")}
              >
                <Text
                  selectable={false}
                  style={[
                    styles.filterPillText,
                    selectedSectionKey === "all" && styles.filterPillTextActive,
                  ]}
                >
                  Все
                </Text>
                <View
                  style={[
                    styles.filterPillCount,
                    selectedSectionKey === "all" && styles.filterPillCountActive,
                  ]}
                >
                  <Text
                    selectable={false}
                    style={[
                      styles.filterPillCountText,
                      selectedSectionKey === "all" && styles.filterPillCountTextActive,
                    ]}
                  >
                    {totalUnitsCount}
                  </Text>
                </View>
              </PressableWrapp>

              {sections.map((section) => {
                const active = selectedSectionKey === section.key;

                return (
                  <PressableWrapp
                    key={section.key}
                    style={[styles.filterPill, active && styles.filterPillActive]}
                    onPress={() => onSelectSection(section.key)}
                  >
                    <Text
                      selectable={false}
                      style={[styles.filterPillText, active && styles.filterPillTextActive]}
                    >
                      {section.label}
                    </Text>
                    <View
                      style={[
                        styles.filterPillCount,
                        active && styles.filterPillCountActive,
                      ]}
                    >
                      <Text
                        selectable={false}
                        style={[
                          styles.filterPillCountText,
                          active && styles.filterPillCountTextActive,
                        ]}
                      >
                        {section.count}
                      </Text>
                    </View>
                  </PressableWrapp>
                );
              })}
            </View>

            {linkPdf ? (
              <PressableWrapp style={styles.outlineChip} onPress={onOpenPdf}>
                <Text selectable={false} style={styles.outlineChipText}>
                  PDF коллекции
                </Text>
              </PressableWrapp>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
};

export default React.memo(CollectionHeaderV2);
